import React from 'react';
import styled from 'styled-components';
import { X, Check } from 'lucide-react';
import { FONT_MAP } from '../theme';
import { useThemeMode } from './themeMode';

/* ============================================================
   Live Tweaks panel — a self-contained styled-components port of
   the reference tweaks-panel. The reference drives the host edit
   protocol (postMessage); here it's a stand-alone floating panel
   with a visible toggle, wired to useTweaks (localStorage-backed).
   Controls + groups mirror the reference App's <TweaksPanel> block.
   ============================================================ */

/* ---- accent / button / rail / bg swatch maps (reference App hexes) ---- */
const ACCENT_HEX = { indigo: '#5b54e6', violet: '#8b3ff0', blue: '#2f74e0', emerald: '#10977a', amber: '#e0922f' };
const BTN_HEX    = { ink: '#1b1e25', accent: '#5b54e6', blue: '#2f74e0', emerald: '#10977a', amber: '#e0922f' };
const RAIL_HEX   = { slate: '#2c3343', ink: '#16181d', plum: '#2c2340', ocean: '#1e2a45', forest: '#1b2e25', light: '#ece8e1' };
const BG_HEX     = { neutral: '#f5f5f7', white: '#fdfdfe', warm: '#f7f2ea', cool: '#eef1f8', mint: '#eef6f1', lilac: '#f4eef9' };
const invert = (m) => Object.fromEntries(Object.entries(m).map(([k, v]) => [v, k]));

/* ---- shell ---- */

const Panel = styled.div`
  position: fixed;
  right: 16px;
  top: 74px;
  z-index: 2147483646;
  width: 280px;
  max-height: calc(100vh - 32px);
  display: flex;
  flex-direction: column;
  background: ${({ theme }) => theme.surface};
  color: ${({ theme }) => theme.text};
  border: 1px solid ${({ theme }) => theme.borderStrong};
  border-radius: ${({ theme }) => theme.rLg};
  box-shadow: ${({ theme }) => theme.shadowLg};
  font-family: ${({ theme }) => theme.fontBody};
  font-size: ${({ theme }) => theme.fsSm};
  line-height: 1.4;
  overflow: hidden;

  .twk-hd {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 10px 8px 10px 14px;
    user-select: none;
    background: ${({ theme }) => theme.surface2};
    border-bottom: 1px solid ${({ theme }) => theme.border};
  }
  .twk-hd b { font-size: 12px; font-weight: 650; letter-spacing: .01em; color: ${({ theme }) => theme.text}; }
  .twk-x {
    appearance: none;
    border: 0;
    background: transparent;
    color: ${({ theme }) => theme.textMuted};
    width: 22px;
    height: 22px;
    border-radius: 6px;
    cursor: pointer;
    display: grid;
    place-items: center;
  }
  .twk-x:hover {
    background: ${({ theme }) => theme.surface3};
    color: ${({ theme }) => theme.text};
  }

  .twk-body {
    padding: 2px 14px 14px;
    display: flex;
    flex-direction: column;
    gap: 10px;
    overflow-y: auto;
    overflow-x: hidden;
    min-height: 0;
    scrollbar-width: thin;
    scrollbar-color: ${({ theme }) => `${theme.textSubtle} transparent`};
    background: ${({ theme }) => theme.surface};
  }
  .twk-body::-webkit-scrollbar { width: 8px; }
  .twk-body::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.borderStrong};
    border-radius: 4px;
    border: 2px solid transparent;
    background-clip: content-box;
  }

  .twk-sect {
    font-size: ${({ theme }) => theme.fsMicro};
    font-weight: 650;
    letter-spacing: .06em;
    text-transform: uppercase;
    color: ${({ theme }) => theme.textSubtle};
    padding: 10px 0 0;
  }
  .twk-sect:first-child { padding-top: 0; }

  .twk-row { display: flex; flex-direction: column; gap: 5px; }
  .twk-lbl {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    color: ${({ theme }) => theme.textMuted};
  }
  .twk-lbl > span:first-child { font-weight: 550; color: ${({ theme }) => theme.text}; }

  .twk-field {
    appearance: none;
    box-sizing: border-box;
    width: 100%;
    min-width: 0;
    height: 26px;
    padding: 0 8px;
    border: 1px solid ${({ theme }) => theme.border};
    border-radius: 7px;
    background: ${({ theme }) => theme.surface2};
    color: ${({ theme }) => theme.text};
    font: inherit;
    outline: none;
  }
  .twk-field:focus {
    border-color: ${({ theme }) => theme.accent};
    background: ${({ theme }) => theme.surface3};
  }
  select.twk-field { padding-right: 22px; }

  .twk-seg {
    position: relative;
    display: flex;
    padding: 2px;
    border-radius: 8px;
    background: ${({ theme }) => theme.surface3};
    user-select: none;
  }
  .twk-seg-thumb {
    position: absolute;
    top: 2px;
    bottom: 2px;
    border-radius: 6px;
    background: ${({ theme }) => theme.surface};
    box-shadow: ${({ theme }) => theme.shadowSm};
    transition: left .15s cubic-bezier(.3,.7,.4,1), width .15s;
  }
  .twk-seg button {
    appearance: none;
    position: relative;
    z-index: 1;
    flex: 1;
    border: 0;
    background: transparent;
    color: ${({ theme }) => theme.text};
    font: inherit;
    font-weight: 550;
    min-height: 22px;
    border-radius: 6px;
    cursor: pointer;
    padding: 4px 6px;
    line-height: 1.2;
    overflow-wrap: anywhere;
  }

  .twk-chips { display: flex; gap: 6px; }
  .twk-chip {
    position: relative;
    appearance: none;
    flex: 1;
    min-width: 0;
    height: 36px;
    padding: 0;
    border: 0;
    border-radius: 6px;
    overflow: hidden;
    cursor: pointer;
    box-shadow: 0 0 0 1px ${({ theme }) => theme.border};
    transition: transform .12s cubic-bezier(.3,.7,.4,1), box-shadow .12s;
  }
  .twk-chip:hover {
    transform: translateY(-1px);
    box-shadow: 0 0 0 1px ${({ theme }) => theme.borderStrong}, ${({ theme }) => theme.shadowSm};
  }
  .twk-chip[data-on="1"] {
    box-shadow:
      0 0 0 1.5px ${({ theme }) => theme.accentStrong},
      ${({ theme }) => theme.shadowSm};
  }
  .twk-chip svg {
    position: absolute;
    top: 5px;
    left: 5px;
    width: 13px;
    height: 13px;
    filter: drop-shadow(0 1px 1px rgba(0, 0, 0, 0.35));
  }
`;

/* ---- control building blocks ---- */
function Section({ label }) { return <div className="twk-sect">{label}</div>; }

function Radio({ label, value, options, onChange }) {
  const opts = options.map((o) => (typeof o === 'object' ? o : { value: o, label: o }));
  const idx = Math.max(0, opts.findIndex((o) => o.value === value));
  const n = opts.length;
  return (
    <div className="twk-row">
      <div className="twk-lbl"><span>{label}</span></div>
      <div className="twk-seg" role="radiogroup">
        <div className="twk-seg-thumb" style={{ left: `calc(2px + ${idx} * (100% - 4px) / ${n})`, width: `calc((100% - 4px) / ${n})` }} />
        {opts.map((o) => (
          <button key={o.value} type="button" role="radio" aria-checked={o.value === value} onClick={(e) => onChange(o.value, e)}>
            {o.label}
          </button>
        ))}
      </div>
    </div>
  );
}

function ColorRow({ label, value, options, onChange }) {
  return (
    <div className="twk-row">
      <div className="twk-lbl"><span>{label}</span></div>
      <div className="twk-chips" role="radiogroup">
        {options.map((hex) => {
          const on = hex.toLowerCase() === String(value).toLowerCase();
          const light = isLight(hex);
          return (
            <button key={hex} type="button" className="twk-chip" role="radio" aria-checked={on}
                    data-on={on ? '1' : '0'} title={hex} style={{ background: hex }} onClick={() => onChange(hex)}>
              {on && <Check color={light ? 'rgba(0,0,0,.78)' : '#fff'} strokeWidth={3} />}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function SelectRow({ label, value, options, onChange }) {
  return (
    <div className="twk-row">
      <div className="twk-lbl"><span>{label}</span></div>
      <select className="twk-field" value={value} onChange={(e) => onChange(e.target.value)}>
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  );
}

function isLight(hex) {
  const h = String(hex).replace('#', '');
  const x = h.length === 3 ? h.replace(/./g, (c) => c + c) : h.padEnd(6, '0');
  const n = parseInt(x.slice(0, 6), 16);
  if (Number.isNaN(n)) return true;
  const r = (n >> 16) & 255, g = (n >> 8) & 255, b = n & 255;
  return r * 299 + g * 587 + b * 114 > 148000;
}

const HEX_TO_ACCENT = invert(ACCENT_HEX);
const HEX_TO_BTN = invert(BTN_HEX);
const HEX_TO_RAIL = invert(RAIL_HEX);
const HEX_TO_BG = invert(BG_HEX);

/* ============================================================ */
export default function TweaksPanel({ tweaks: t, setTweak, onClose }) {
  const { setThemeWithBubble } = useThemeMode();

  return (
    <Panel role="dialog" aria-label="Tweaks">
      <div className="twk-hd">
        <b>Tweaks</b>
        <button className="twk-x" aria-label="Close tweaks" onClick={onClose}><X size={14} /></button>
      </div>
      <div className="twk-body">
        <Section label="Theme" />
        <Radio label="Mode" value={t.theme} options={['light', 'dark']} onChange={(v, e) => setThemeWithBubble(v, e)} />
        <ColorRow label="Accent" value={ACCENT_HEX[t.accent]} options={Object.values(ACCENT_HEX)}
                  onChange={(v) => setTweak('accent', HEX_TO_ACCENT[v] || 'indigo')} />
        <ColorRow label="Primary button" value={BTN_HEX[t.btn]} options={Object.values(BTN_HEX)}
                  onChange={(v) => setTweak('btn', HEX_TO_BTN[v] || 'ink')} />

        <Section label="Layout" />
        <Radio label="Density" value={t.density} options={['comfortable', 'compact']} onChange={(v) => setTweak('density', v)} />
        <Radio label="List rows" value={t.list} options={['comfortable', 'compact']} onChange={(v) => setTweak('list', v)} />
        <Radio label="Sidebar" value={t.sidebar} options={['labeled', 'icon']} onChange={(v) => setTweak('sidebar', v)} />
        <ColorRow label="Sidebar color" value={RAIL_HEX[t.rail]} options={Object.values(RAIL_HEX)}
                  onChange={(v) => setTweak('rail', HEX_TO_RAIL[v] || 'slate')} />
        <ColorRow label="Background" value={BG_HEX[t.bg]} options={Object.values(BG_HEX)}
                  onChange={(v) => setTweak('bg', HEX_TO_BG[v] || 'mint')} />

        <Section label="Style" />
        <Radio label="Cards" value={t.cards} options={['elevated', 'flat']} onChange={(v) => setTweak('cards', v)} />
        <Radio label="Charts" value={t.chart} options={['donut', 'bars']} onChange={(v) => setTweak('chart', v)} />
        <SelectRow label="Typeface" value={t.font}
                   options={Object.keys(FONT_MAP).map((k) => ({ value: k, label: FONT_MAP[k].label }))}
                   onChange={(v) => setTweak('font', v)} />
      </div>
    </Panel>
  );
}
